/**
 * Design System Section Registry
 * ------------------------------
 * Single source of truth for the `/owner/design-system` page and the sidebar
 * sub-menu. Adding a section here automatically surfaces it in both places.
 *
 * Shape:
 *   {
 *     group:        'foundations' | 'primitives' | 'task' | 'panes' | 'date',
 *     id:           stable hash id used for anchor links (#id)
 *     label:        shown in the sidebar tree and section header
 *     eyebrow?:     small all-caps label above the title (default derived)
 *     subtitle?:    one-line description shown under the title
 *     testId:       stable data-testid for the section wrapper
 *     defaultOpen?: whether the disclosure starts expanded
 *   }
 */

export const DS_GROUPS = [
  { key: 'foundations', label: 'Foundations', hint: 'Tokens' },
  { key: 'colors',      label: 'Color System', hint: 'Every registered color + raw code' },
  { key: 'primitives',  label: 'Primitives',  hint: 'Atoms' },
  { key: 'task',        label: 'Task identity', hint: 'V4 canonical card' },
  { key: 'panes',       label: 'Panes',       hint: 'Layout' },
  { key: 'date',        label: 'Date picker', hint: 'V4 visible-grid' },
  { key: 'layouts',     label: 'App Layouts', hint: 'Master page shells' },
];

export const DS_SECTIONS = [
  // Foundations
  { group: 'foundations', id: 'surfaces',  label: 'Surface tokens',    testId: 'ds-section-surfaces',        defaultOpen: true  },
  { group: 'foundations', id: 'text',      label: 'Text tokens',       testId: 'ds-section-text',            defaultOpen: false },
  { group: 'foundations', id: 'priority',  label: 'Priority map',      testId: 'ds-section-priority',        defaultOpen: false },
  { group: 'foundations', id: 'states',    label: 'Task state map',    testId: 'ds-section-states',          defaultOpen: false },
  { group: 'foundations', id: 'intents',   label: 'Intent vocabulary', testId: 'ds-section-intents',         defaultOpen: false },
  { group: 'foundations', id: 'tones',     label: 'Agent tones',       testId: 'ds-section-agent-tones',     defaultOpen: false },
  { group: 'foundations', id: 'effort',    label: 'Effort scale',      testId: 'ds-section-effort',          defaultOpen: false },

  // Color System — every color currently registered in the DS with raw-code expanders
  { group: 'colors', id: 'color-system', label: 'All color tokens + raw code', testId: 'ds-section-color-system', defaultOpen: true },

  // Primitives
  { group: 'primitives', id: 'buttons',           label: 'OrkaButton',     testId: 'ds-section-buttons',       defaultOpen: false },
  { group: 'primitives', id: 'badges',            label: 'OrkaBadge',      testId: 'ds-section-badges',        defaultOpen: false },
  { group: 'primitives', id: 'disclosure',        label: 'OrkaDisclosure', testId: 'ds-section-disclosure',    defaultOpen: false },
  { group: 'primitives', id: 'surface-component', label: 'OrkaSurface',    testId: 'ds-section-surface-component', defaultOpen: false },
  { group: 'primitives', id: 'phase2-atoms',      label: 'Priority / State / Intent chips', testId: 'ds-section-phase2-atoms', defaultOpen: false },

  // Task identity — TWO distinct families.
  //
  //   1) **V5 — Task Card (canonical)** — single unified showcase that
  //      consolidates Base, 60-min spotlight, and the six effort tiers.
  //      Theme follows the design-system page Light/Dark toggle.
  //
  //   2) **Field-Groups family** (DEPRECATED) — older OrkaTaskCard with
  //      nested field groups. Kept for visual reference only; do not use in
  //      new screens.
  { group: 'task', id: 'task-card-v5',         label: 'V5 — Task Card (canonical · Base · 60-min · Effort tiers)',    testId: 'ds-section-task-card-v5',         defaultOpen: true  },
  { group: 'task', id: 'task-card-closed',     label: 'Field-Groups · Closed (DEPRECATED — do not use)',              testId: 'ds-section-task-card-closed',     defaultOpen: false },
  { group: 'task', id: 'task-card-open',       label: 'Field-Groups · Open (DEPRECATED — do not use)',                testId: 'ds-section-task-card-open',       defaultOpen: false },
  { group: 'task', id: 'task-card-variants',   label: 'Field-Groups · Variants (DEPRECATED — do not use)',            testId: 'ds-section-task-card-variants',   defaultOpen: false },
  { group: 'task', id: 'task-field-groups',    label: 'Field groups registry',         testId: 'ds-section-task-field-groups',    defaultOpen: false },
  { group: 'task', id: 'task-row',             label: 'OrkaTaskRow + IdentityStrip',   testId: 'ds-section-task-row',             defaultOpen: false },

  // Panes
  { group: 'panes', id: 'pane', label: 'OrkaPane', testId: 'ds-section-pane', defaultOpen: false },
  { group: 'panes', id: 'daily-panes-desktop',       label: 'Daily panes — desktop layout',  testId: 'ds-section-daily-panes-desktop',       defaultOpen: true  },
  { group: 'panes', id: 'daily-panes-states',        label: 'Daily panes — empty & loading', testId: 'ds-section-daily-panes-states',        defaultOpen: false },
  { group: 'panes', id: 'daily-panes-mobile',        label: 'Daily panes — mobile stack',    testId: 'ds-section-daily-panes-mobile',        defaultOpen: false },
  { group: 'panes', id: 'daily-panes-implementation',label: 'Daily panes — implementation',  testId: 'ds-section-daily-panes-implementation',defaultOpen: false },

  // Date picker (V4 — broken out into sub-sections matching the rest of the system)
  { group: 'date', id: 'date-picker-calendar',   label: 'Calendar — modes',    testId: 'ds-section-date-picker-calendar',   defaultOpen: true  },
  { group: 'date', id: 'date-picker-triggers',   label: 'Trigger variants',    testId: 'ds-section-date-picker-triggers',   defaultOpen: false },
  { group: 'date', id: 'date-picker-cells',      label: 'Cell state matrix',   testId: 'ds-section-date-picker-cells',      defaultOpen: false },
  { group: 'date', id: 'date-picker-grids',      label: 'Visible grid sizes',  testId: 'ds-section-date-picker-grids',      defaultOpen: false },
  { group: 'date', id: 'date-picker-legend',     label: 'Effort legend',       testId: 'ds-section-date-picker-legend',     defaultOpen: false },
  { group: 'date', id: 'date-picker-decision',   label: 'Overload decision',   testId: 'ds-section-date-picker-decision',   defaultOpen: false },

  // App Layouts (master page shells — App Page variants)
  { group: 'layouts', id: 'app-shell-variants', label: 'Whole-app shell variants', testId: 'ds-section-app-shell-variants', defaultOpen: true },
  { group: 'layouts', id: 'app-shell-anatomy',  label: 'Left panel anatomy',       testId: 'ds-section-app-shell-anatomy',  defaultOpen: true },
  { group: 'layouts', id: 'app-layouts', label: 'App Page variants', testId: 'ds-section-app-layouts', defaultOpen: true },
];

export function sectionsForGroup(group) {
  return DS_SECTIONS.filter((s) => s.group === group);
}
